import { html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../utils/translator.helper';
import { productType } from '../types';
import { setTranslation } from '../utils/translate';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { buildHash, NavigationHelper } from '@nirvana/shared/navigation';
import { isEmpty } from 'lodash';
import { Language } from '../../../utils/i18n.utils';
import { ButtonColors, ButtonThemes, ButtonTypes } from '../../gzButton/types';
import { ButtonFill, LinkColor, LinkType, Target } from '../../gzLinkComponent/types';

@customElement('gz-help-and-support')
export class GzHelpAndSupportWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  company = productType.CLOUD;

  @property({ type: Object, attribute: true, reflect: false })
  translations: any = null;

  @property({ type: String, attribute: true, reflect: true })
  productVersion = '';

  @property({ type: Object, attribute: true, reflect: false })
  productFeatures: any = null;

  @property({ type: Object, attribute: true, reflect: false })
  userRights: any = null;

  @property({ type: String, attribute: true, reflect: true })
  language: Language = Language.English;

  //TODO when we know and have support for all languages, move this map to /apps/webcomponents/src/utils/i18n.utils.ts
  @state()
  formattedLanguage = new Map<Language, string>([
    [Language.English, 'en'],
    [Language.French, 'fr'],
    [Language.German, 'de'],
    [Language.Spanish, 'es'],
    [Language.Romanian, 'ro'],
    [Language.Polish, 'en'],
    [Language.Portuguese, 'pt'],
    [Language.Italian, 'it'],
    [Language.Russian, 'en'],
    [Language.Czech, 'en'],
    [Language.Chinese, 'en'],
    [Language.Korean, 'en'],
    [Language.Turkish, 'en'],
    [Language.Vietnamese, 'en'],
    [Language.Japanese, 'en'],
    [Language.Default, 'en'],
  ]);

  connectedCallback() {
    super.connectedCallback();
    this.translations = setTranslation(this.company, this.formattedLanguage.get(this.language));
  }
  updated(properties: PropertyValues): void {
    if (properties.has('company') || properties.has('language')) {
      this.translations = setTranslation(this.company, this.formattedLanguage.get(this.language));
    }
  }

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="main-container">
        <gz-title fontSize="${FontSizes.TITLE}" class="mb-3" fontWeight="${FontWeights.MEDIUM}"
          >${translateFn('Help and support')}</gz-title
        >
        ${this.renderHeaderBanner()}
        <gz-help-and-support-tabs
          .features="${this.productFeatures}"
          .rights="${this.userRights}"
          .cards="${this.translations.tabs}"
        ></gz-help-and-support-tabs>

        ${this.renderMasterClassBanner()}
        <gz-title fontSize="${FontSizes.TITLE}" class="mb-3 mt-6" fontWeight="${FontWeights.MEDIUM}"
          >${translateFn('Technical Support')}</gz-title
        >

        <div class="help-and-support-cards-container grid-layout">
          ${this.renderContactUsCard()} ${this.renderTechnicalAssistanceCard()} ${this.renderMalwareAssistanceCard()}
          ${this.renderUninstallBestCard()}
        </div>

        ${this.renderFaqBanner()}

        <div class="help-and-support-cards-container grid-layout">
          ${this.renderFeedbackCard()} ${this.renderEnterpriseCommunityCard()} ${this.renderLegal()}
        </div>
      </div>
    `;
  }

  private renderHeaderBanner(): TemplateResult {
    const linkButtons: { link: any; text: string; callback?: () => void }[] = [
      {
        link: null,
        text: "What's New",
        callback: () => this.showWhatsNewPanel(),
      },
      {
        link: this.translations.gettingStarted.releaseNotes.link,
        text: 'Release Notes',
      },
      {
        link: this.translations.gettingStarted.supportCenter.link,
        text: 'Support center',
      },
    ];

    return html`
      <div class="card header-banner">
        <gz-title fontSize="${FontSizes.HEADING_3}" fontWeight="${FontWeights.MEDIUM}" class="mb-1"
          >${translateFn('Getting Started')}</gz-title
        >
        ${!isEmpty(this.productVersion) ? this.renderProductVersion() : ''}
        <gz-text fontSize="${FontSizes.HEADING_6}"
          >${translateFn('Choose one of the options below to familiarize yourself with GravityZone.')}</gz-text
        >
        <div class="d-flex btns-container mb-4">
          <gz-button @click="${() => this.showGuideWindow()}">${translateFn('Essential Steps')}</gz-button>
          ${this.renderGzButton('onboarding', this.translations.gettingStarted.onboarding.link)}
        </div>
        <gz-text fontSize="${FontSizes.HEADING_6}"
          >${translateFn(
            'Keep up with the latest GravityZone updates or access the official product documentation.'
          )}</gz-text
        >
        <div class="links-container mt-2">
          ${linkButtons.map(item =>
            this.renderGzLink(
              item.text,
              item.link,
              FontSizes.PARAGRAPH_11,
              item.callback ? item.callback : undefined,
              'text-uppercase'
            )
          )}
        </div>
      </div>
    `;
  }

  private renderProductVersion(): TemplateResult {
    return html`
      <gz-title fontSize="${FontSizes.HEADING_6}" class="mb-3" fontWeight="${FontWeights.MEDIUM}"
        >${translateFn('Your GravityZone version:') + ' ' + this.productVersion}</gz-title
      >
    `;
  }

  private renderEnterpriseLink(): TemplateResult {
    const link = document.createElement('span');
    link.innerHTML = translateFn('Read our {startLink} Enterprise Support Policy {endLink}', {
      startLink:
        '<gz-link type="underline" href="https://www.bitdefender.com/site/view/enterprise-support-policies.html"><span>',
      endLink: '</span></gz-link>',
    });

    return html`<gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3">${link}</gz-text`;
  }

  private renderContactUsCard(): TemplateResult {
    const gzContent = html`
      <div class="btns-container">
        ${this.renderGzButton(
          'GravityZone VA LogCollector',
          this.translations.contactUs.btn_0?.link,
          ButtonColors.PRIMARY,
          ButtonThemes.LIGHT
        )}
      </div>
    `;

    const buttons: { link: any; os: string }[] = [
      {
        link: this.translations.contactUs.btn_1.link,
        os: 'Windows',
      },
      {
        link: this.translations.contactUs.btn_2.link,
        os: 'Mac',
      },
      {
        link: this.translations.contactUs.btn_3.link,
        os: 'Linux',
      },
    ];

    return html`
      <div class="card contact-us-card border-radius">
        <gz-title fontSize="${FontSizes.HEADING_5}">${translateFn('Helpful resources')}</gz-title>
        ${this.renderEnterpriseLink()}
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-4"
          >${translateFn(
            'Collect logs and send them to Bitdefender when you open a case for an even faster resolution.'
          )}</gz-text
        >

        ${this.company === productType.GZ ? gzContent : ''}

        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3">${translateFn('Security agent Support Tool:')}</gz-text>

        <div class="contact-us-buttons-wrapper">
          <div class="btns-container">${buttons.map(item => this.renderGzButton(item.os, item.link))}</div>
        </div>
      </div>
    `;
  }

  private renderTechnicalAssistanceCard(): TemplateResult {
    return html`
      <div class="card technical-assistance-card border-radius">
        <gz-title fontSize="${FontSizes.HEADING_5}">${translateFn('Technical Assistance')}</gz-title>
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3"
          >${translateFn(
            'If you need assistance, please open a support ticket. Our specialists will take care of your case as soon as possible.'
          )}</gz-text
        >
        <div class="btns-container">
          <gz-button @click="${() => this.openLink(this.translations.technicalAssistance.btn_1.link)}"
            >${translateFn('Open Support Ticket')}</gz-button
          >
        </div>
      </div>
    `;
  }

  private renderMalwareAssistanceCard(): TemplateResult {
    return html`
      <div class="card malware-assistance-card border-radius">
        <gz-title fontSize="${FontSizes.HEADING_5}">${translateFn('Malware Assistance')}</gz-title>
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3" fontWeight="${FontWeights.MEDIUM}"
          >${translateFn('False positive')}</gz-text
        >
        <gz-text fontSize="${FontSizes.HEADING_6}"
          >${translateFn(
            'In rare cases, in-house developed applications can be detected as malicious though they are not. You can report such cases to Bitdefender for analysis.'
          )}</gz-text
        >
        <div class="btns-container">
          ${this.renderGzButton(
            'Submit sample',
            this.translations.malwareAssistance.btn_1.link,
            ButtonColors.PRIMARY,
            ButtonThemes.LIGHT,
            ButtonTypes.FULL
          )}
          ${this.renderGzButton('Guidelines', this.translations.malwareAssistance.btn_2.link)}
        </div>

        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3" fontWeight="${FontWeights.MEDIUM}"
          >${translateFn('Bdsyslog scanning tool')}</gz-text
        >
        <gz-text fontSize="${FontSizes.HEADING_6}"
          >${translateFn('This tool gathers system settings and logs necessary for troubleshooting.')}</gz-text
        >
        <div class="btns-container">
          ${this.renderGzButton('Guidelines', this.translations.malwareAssistance.btn_3.link)}
          ${this.renderGzButton('Download', this.translations.malwareAssistance.btn_4.link)}
        </div>
      </div>
    `;
  }

  private renderUninstallBestCard(): TemplateResult {
    return html`
      <div class="card uninstall-best-card border-radius">
        <gz-title fontSize="${FontSizes.HEADING_5}">${translateFn('Uninstall Bitdefender agents')}</gz-title>
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3"
          >${translateFn(
            'If the situation imposes removing the security agent from the endpoint, you can use multiple methods.'
          )}</gz-text
        >
        <div class="btns-container">
          ${this.renderGzButton('Guidelines', this.translations.uninstallBest.btn_1.link)}
        </div>

        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3" fontWeight="${FontWeights.MEDIUM}"
          >${translateFn('Uninstall Tool')}</gz-text
        >
        <gz-text fontSize="${FontSizes.HEADING_6}"
          >${translateFn(
            'Use Bitdefender Uninstall Tool for Windows endpoints when other options are unavailable or unsuccessful.'
          )}</gz-text
        >
        <div class="btns-container">
          ${this.renderGzButton('Guidelines', this.translations.uninstallBest.btn_2.link)}
          ${this.renderGzButton('Download', this.translations.uninstallBest.btn_3.link)}
        </div>
      </div>
    `;
  }

  private renderFeedbackCard(): TemplateResult {
    return html`
      <div class="card feedback-card border-radius">
        <gz-title fontSize="${FontSizes.HEADING_5}">${translateFn('Feedback')}</gz-title>
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3"
          >${translateFn('Got feedback? Submit your comments and suggestions in the form below.')}</gz-text
        >
        <div class="btns-container">
          <gz-link
            color="${LinkColor.SECONDARY}"
            type="${LinkType.BUTTON}"
            target="${Target.SELF}"
            href="${buildHash('/feedback')}"
            >${translateFn('Feedback Form')}</gz-link
          >
        </div>
      </div>
    `;
  }

  private renderEnterpriseCommunityCard(): TemplateResult {
    return html`
      <div class="card enterprise-community-card border-radius">
        <gz-title fontSize="${FontSizes.HEADING_5}">${translateFn('Enterprise Community')}</gz-title>
        <gz-text fontSize="${FontSizes.HEADING_6}" class="mt-3"
          >${translateFn(
            'A place to find solutions, ask questions, share ideas and collaborate with other Bitdefender customers.'
          )}</gz-text
        >
        <div class="btns-container">
          ${this.renderGzButton(
            'Explore',
            this.translations.enterpriseCommunity.btn.link,
            ButtonColors.SECONDARY,
            ButtonThemes.DARK,
            ButtonTypes.OUTLINE,
            'explore'
          )}
        </div>
      </div>
    `;
  }

  private renderLegal(): TemplateResult {
    const resellingTerms = html`
      ${this.renderGzLink(
        'Bitdefender Reselling Terms and Conditions',
        this.translations.legal.btn_4?.link,
        FontSizes.HEADING_6
      )}
    `;

    const links: { link: string; text: string }[] = [
      {
        link: this.translations.legal.btn_1.link,
        text: 'License and services agreement',
      },
      {
        link: this.translations.legal.btn_2.link,
        text: 'Bitdefender privacy policies',
      },
      {
        link: this.translations.legal.btn_3.link,
        text: 'Open source software used by Bitdefender',
      },
    ];

    return html`
      <div class="card legal-card border-radius">
        <gz-title fontSize="${FontSizes.HEADING_5}">${translateFn('Legal')}</gz-title>

        <div class="btns-container mt-2">
          ${links.map(item => this.renderGzLink(item.text, item.link, FontSizes.HEADING_6))}
          ${Application.Main.company.isMspPartner() ? resellingTerms : nothing}
        </div>
      </div>
    `;
  }

  private renderMasterClassBanner(): TemplateResult {
    return html`
      <div class="card master-class-banner banner border-radius">
        <gz-title class="mb-3" fontSize="${FontSizes.HEADING_3}" fontWeight="${FontWeights.MEDIUM}"
          >${translateFn('Bitdefender Masterclass')}</gz-title
        >
        <gz-text fontSize="${FontSizes.HEADING_6}"
          >${translateFn(
            'Join us for free in our recurring Masterclass trainings! Access instructor and self-paced technical live webinars to help you become an expert in Bitdefender products and solutions. Each online class is taught by Bitdefender expert cybersecurity professionals with Q&A opportunities.'
          )}</gz-text
        >
        <div class="btns-container mt-3">
          ${this.renderGzButton(
            'Explore',
            this.translations.bitdefenderMasterclass.btn.link,
            ButtonColors.PRIMARY,
            ButtonThemes.DARK,
            ButtonTypes.FULL,
            'explore'
          )}
        </div>
      </div>
    `;
  }

  private renderFaqBanner(): TemplateResult {
    let faqLinks = Object.values(this.translations.faq);
    faqLinks = this.handleFaqLinksVisibility(faqLinks);

    return html`
      <div class="card faq-banner banner border-radius">
        <gz-title class="mb-3" fontSize="${FontSizes.HEADING_3}" fontWeight="${FontWeights.MEDIUM}"
          >${translateFn('FAQ')}</gz-title
        >
        <div class="d-inline-flex flex-column">${this.renderLinks(faqLinks)}</div>
        <div class="btns-container">
          ${this.renderGzButton(
            'Explore',
            this.translations.faq.btn.link,
            ButtonColors.PRIMARY,
            ButtonThemes.DARK,
            ButtonTypes.FULL,
            'explore'
          )}
        </div>
      </div>
    `;
  }

  private renderLinks(faqLinks): (TemplateResult | typeof nothing)[] {
    return Object.entries(faqLinks).map((faqLink: [string, { text: string; link: string }]) =>
      this.renderLinkItem(faqLink)
    );
  }

  private handleFaqLinksVisibility(faqLinks) {
    const returnedLinks = faqLinks[0];
    if (!this.productFeatures) {
      return { ...returnedLinks };
    }

    const cloudLinks = this.company === productType.GZ ? { twoFA: undefined, cloudSecurityProducts: undefined } : {};

    // check if features are available
    if (this.productFeatures.patchManagementVisible && !this.productFeatures.encryptionVisible) {
      return { ...returnedLinks, fullDiskEncryption: undefined, ...cloudLinks };
    } else if (this.productFeatures.encryptionVisible && !this.productFeatures.patchManagementVisible) {
      return { ...returnedLinks, patchManagement: undefined, ...cloudLinks };
    } else if (!this.productFeatures.patchManagementVisible && !this.productFeatures.encryptionVisible) {
      return { ...returnedLinks, patchManagement: undefined, fullDiskEncryption: undefined, ...cloudLinks };
    } else {
      return { ...returnedLinks, ...cloudLinks };
    }
  }

  private renderLinkItem(item: [string, { text: string; link: string }]): TemplateResult | typeof nothing {
    const [, data] = item;
    return data !== undefined
      ? html`<gz-link type="underline" fontSize="${FontSizes.HEADING_6}" href="${data.link}" class="mb-1">
          <span>${data.text}</span>
        </gz-link>`
      : nothing;
  }

  private renderGzLink(
    text: string,
    link?: string,
    fontSize?: FontSizes,
    callback?: () => void,
    className?: string
  ): TemplateResult {
    return html`
      <gz-link href="${link}" class="${className}" fontSize="${fontSize}" @click="${callback}">
        <span>${translateFn(`${text}`)}</span>
      </gz-link>
    `;
  }

  private renderGzButton(
    text: string,
    link: string,
    color: ButtonColors = ButtonColors.SECONDARY,
    theme: ButtonThemes = ButtonThemes.DARK,
    type: ButtonTypes = ButtonTypes.OUTLINE,
    className?: string
  ): TemplateResult {
    return html`
      <gz-button
        color="${color}"
        class="${className}"
        theme="${theme}"
        type="${type}"
        @click="${() => this.openLink(link)}"
        >${translateFn(`${text}`)}</gz-button
      >
    `;
  }

  private showWhatsNewPanel(): void {
    NavigationHelper.showWhatsNew();
  }

  private showGuideWindow(): void {
    Application.modules.licenseTerms.module.guideWindow.show();
  }

  private openLink(link: string): void {
    window.open(link, '_blank');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-help-and-support': GzHelpAndSupportWebComponent;
  }
}
